import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useState, useId } from 'react';
function CheckIcon() {
    return (_jsx("svg", { width: "14", height: "14", viewBox: "0 0 14 14", fill: "none", "aria-hidden": "true", children: _jsx("path", { d: "M2.5 7l3 3 6-6", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }) }));
}
function AlertIcon() {
    return (_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 14 14", fill: "none", "aria-hidden": "true", children: [_jsx("circle", { cx: "7", cy: "7", r: "6", stroke: "currentColor", strokeWidth: "1.5" }), _jsx("path", { d: "M7 4.5V7.5M7 9.5v.5", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" })] }));
}
export const Input = forwardRef(({ label, hint, error, success, state: stateProp, leadingIcon, trailingElement, id: idProp, disabled, onFocus, onBlur, style, ...props }, ref) => {
    const autoId = useId();
    const id = idProp ?? autoId;
    const hintId = `${id}-hint`;
    const [focused, setFocused] = useState(false);
    const state = stateProp ?? (error ? 'error' : success ? 'success' : 'default');
    const borderColor = focused
        ? state === 'error'
            ? '#ef4444'
            : state === 'success'
                ? '#00d4aa'
                : 'var(--color-accent-primary, #00d4aa)'
        : state === 'error'
            ? 'rgba(239,68,68,0.5)'
            : state === 'success'
                ? 'rgba(0,212,170,0.4)'
                : 'var(--color-border, #2a2a2e)';
    const focusShadow = focused
        ? state === 'error'
            ? '0 0 0 3px rgba(239,68,68,0.15)'
            : state === 'success'
                ? '0 0 0 3px rgba(0,212,170,0.15)'
                : '0 0 0 3px rgba(0,212,170,0.12)'
        : 'none';
    const trailingIcon = state === 'error' ? (_jsx("span", { style: { color: '#ef4444' }, children: _jsx(AlertIcon, {}) })) : state === 'success' ? (_jsx("span", { style: { color: '#00d4aa' }, children: _jsx(CheckIcon, {}) })) : (trailingElement);
    return (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: '6px', ...style }, children: [label && (_jsx("label", { htmlFor: id, style: {
                    fontSize: 'var(--text-caption, 13px)',
                    fontWeight: 500,
                    color: disabled ? 'var(--color-text-faint)' : 'var(--color-text-muted)',
                    fontFamily: 'var(--font-body)',
                    userSelect: 'none',
                }, children: label })), _jsxs("div", { style: { position: 'relative', display: 'flex', alignItems: 'center' }, children: [leadingIcon && (_jsx("span", { "aria-hidden": "true", style: {
                            position: 'absolute',
                            left: '12px',
                            color: 'var(--color-text-faint)',
                            display: 'flex',
                            alignItems: 'center',
                            pointerEvents: 'none',
                            zIndex: 1,
                        }, children: leadingIcon })), _jsx("input", { ref: ref, id: id, disabled: disabled, "aria-describedby": hint || error || success ? hintId : undefined, "aria-invalid": state === 'error' ? true : undefined, onFocus: (e) => {
                            setFocused(true);
                            onFocus?.(e);
                        }, onBlur: (e) => {
                            setFocused(false);
                            onBlur?.(e);
                        }, style: {
                            width: '100%',
                            height: '44px',
                            padding: `0 ${trailingIcon ? '40px' : '14px'} 0 ${leadingIcon ? '40px' : '14px'}`,
                            background: disabled ? 'var(--color-bg-base)' : 'var(--color-bg-input, #0f0f11)',
                            border: `1px solid ${borderColor}`,
                            borderRadius: 'var(--radius-md, 8px)',
                            color: disabled ? 'var(--color-text-faint)' : 'var(--color-text)',
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-body, 15px)',
                            outline: 'none',
                            boxShadow: focusShadow,
                            transition: 'border-color 150ms ease, box-shadow 150ms ease',
                            opacity: disabled ? 0.5 : 1,
                            cursor: disabled ? 'not-allowed' : 'text',
                        }, ...props }), trailingIcon && (_jsx("span", { "aria-hidden": "true", style: {
                            position: 'absolute',
                            right: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            pointerEvents: 'none',
                            zIndex: 1,
                        }, children: trailingIcon }))] }), (hint || error || success) && (_jsx("p", { id: hintId, style: {
                    fontSize: 'var(--text-xs, 12px)',
                    color: error ? '#ef4444' : success ? '#00d4aa' : 'var(--color-text-faint)',
                    fontFamily: 'var(--font-body)',
                    margin: 0,
                }, children: error ?? success ?? hint }))] }));
});
Input.displayName = 'Input';
//# sourceMappingURL=Input.js.map